<main id="main-content" class="main-content">
  @if (errorList.length > 0) {
    <div class="red-warning">
      <mat-icon class="red-warn-icon">warning</mat-icon>
      <div class="red-warn-text">
        <span class="red-title">Incomplete section</span>
        <span class="red-text">
          The following fields are missing details. Please complete them before proceeding to the next section:
          <ul>
            @for (error of errorList; track error) {
              <li>{{error | titlecase}}</li>
            }
          </ul>
        </span>
      </div>
    </div>
  }
  <div class="subtitle"><span class="red-normal">*</span>Indicates required field</div>
  <h2 class="form-header">2. Burned Area Description</h2>

  <mat-card [formGroup]="form">
    <div class="title">FIRE DETAILS</div>

    <!-- Blue Box -->
    <div class="blue-warning">
      <mat-icon class="blue-warn-icon">info_outline</mat-icon>
      <div class="support-text">
        The fire details have been prefilled with data from the <b>BAER Fire Tracker</b>, where applicable.
      </div>
    </div>

    <div class="whole-line">
      <mat-form-field appearance="outline" class="whole-field">
        <mat-label>Fire or Complex Name</mat-label>
        <input matInput formControlName="baer_name" required aria-label="Fire or Complex Name">
        <mat-error>Required Field</mat-error>
      </mat-form-field>
    </div>

    <div class="whole-line">
      <mat-form-field appearance="outline" class="short-field">
        <mat-label>Fire Number</mat-label>
        <input matInput formControlName="fire_number" required aria-label="Fire Number">
        <mat-error>Required Field</mat-error>
      </mat-form-field>
      <mat-form-field appearance="outline" class="short-field">
        <mat-label>Fire Incident Job Code</mat-label>
        <input matInput formControlName="job_code" required aria-label="Fire Incident Job Code">
        <mat-error>Required Field</mat-error>
      </mat-form-field>
    </div>
    <div class="whole-line">
      <mat-form-field appearance="outline" class="short-field">
        <mat-label>Fire Start Date</mat-label>
        <input matInput formControlName="fire_discovery_date" required [matDatepicker]="picker1"
          placeholder="MM/dd/yyyy" aria-label="Fire Start Date">
          <mat-datepicker-toggle matIconSuffix [for]="picker1"></mat-datepicker-toggle>
          <mat-datepicker #picker1></mat-datepicker>
          <mat-error>Required Field</mat-error>
        </mat-form-field>
        <mat-form-field appearance="outline" class="short-field">
          <mat-label>Est. Containment Date</mat-label>
          <input matInput formControlName="containment_date_est" required [matDatepicker]="picker2"
            placeholder="MM/dd/yyyy" aria-label="Estimated Containment Date">
            <mat-datepicker-toggle matIconSuffix [for]="picker2"></mat-datepicker-toggle>
            <mat-datepicker #picker2></mat-datepicker>
            <mat-error>Required Field</mat-error>
          </mat-form-field>
        </div>
        <div class="whole-line">
          <b style="font-size: 16px; margin-left: 15px;">@if (!optional()) {
            <span class="red">*</span>
          }Containment Status</b><br>
          <mat-form-field appearance="outline" class="short-field">
            <mat-label>% Contained</mat-label>
            <input formControlName="percent_contained" matInput aria-label="percentContained" mask="percent"
              suffix="%" required>
            </mat-form-field>
            <mat-form-field appearance="outline" class="short-field">
              <mat-label>As of (Date)</mat-label>
              <input matInput formControlName="containment_date" required [matDatepicker]="picker3"
                placeholder="MM/dd/yyyy" aria-label="As of Date">
                <mat-datepicker-toggle matIconSuffix [for]="picker3"></mat-datepicker-toggle>
                <mat-datepicker #picker3></mat-datepicker>
                <mat-error>Required Field</mat-error>
              </mat-form-field>
            </div>

            <div class="bar">&nbsp;</div>
            <div class="title">FIRE LOCATION <span class="gis-source-text"><b>Data source:</b> GIS Database</span></div>
            @if (report().assessment) {
              <div style="display: flex; flex-wrap:wrap">
                <div class="data-div">
                  <p class="data-title">Region</p>
                  <p class="data-info">{{report().assessment.region ?? "--"}}</p>
                </div>
                <div class="data-div">
                  <p class="data-title">State of Fire Origin</p>
                  <p class="data-info">{{report().assessment.IRWIN_POO_state ?? "--"}}</p>
                </div>
                <div class="data-div">
                  <p class="data-title">Forest(s)</p>
                  <p class="data-info">{{report().assessment.forest_name ?? "--"}}</p>
                </div>
                <div class="data-div">
                  <p class="data-title">State(s) Affected</p>
                  <p class="data-info">{{states.join(", ") | empty_check}}</p>
                </div>
                <div class="data-div">
                  <p class="data-title">Counties</p>
                  <p class="data-info">{{counties.join(", ") | empty_check}}</p>
                </div>
                <div class="data-div">
                  <p class="data-title">Ranger District(s)</p>
                  <p class="data-info">{{rangerDistricts.join(", ") | empty_check}}</p>
                </div>
              </div>
            }

            @if (!simple()) {
              <div class="bar">&nbsp;</div>
              <div class="title right">@if (!optional()) {
                <span class="red">*</span>
                }Subwatershed Numbers (All NFS)@if (optional()) {
                <span class="optional">(Optional)</span>
              }</div>
              <div class="text-div">
                <b>Acres Burned by Subwatershed</b>
                <br>
                  <span class="gray-text">Based on all Soil Burn Severity categories: developed, unburned/very low, low,
                    moderate,
                  high.</span>
                </div>
                <!-- Yellow Banner -->
                <ng-container *ngTemplateOutlet="gisWarn"></ng-container>


                <!-- Table HU12# -->
                <div class="table-container">
                  <table>
                    <tr class="table-header">
                      <th scope="col" style="display: flex">
                        <div class="vert-center">HU#12</div>
                        <div class="vert-center">
                          <button
                            type="button"
                            mat-icon-button
                            class="info-blue-header"
                            matTooltip='12-digit Hydrologic number'
                            matTooltipClass="tooltipClass"
                            aria-label="12-digit Hydrologic number"
                            >
                            <mat-icon>info_outline</mat-icon>
                          </button>
                        </div>
                      </th>
                      <th scope="col">Subwatershed Name</th>
                      <th scope="col">Total Acres</th>
                      <th scope="col">Acres Burned</th>
                      <th scope="col">% Subwatershed Burned</th>
                      <td></td>
                    </tr>
                    <ng-container formArrayName="subwatershed">
                      @for (data of subwatershed.controls; track data; let i = $index) {
                        <tr [formGroupName]="i" class="table-row">
                          <td>
                            <mat-form-field appearance="outline" class="example">
                              <input formControlName="HU12" matInput aria-label="HU12" mask="000000000000">
                            </mat-form-field>
                          </td>
                          <td>
                            <mat-form-field appearance="outline" class="example">
                              <input formControlName="name" matInput aria-label="name">
                            </mat-form-field>
                          </td>
                          <td>
                            <mat-form-field appearance="outline" class="example small-field">
                              <input formControlName="totalAcres" matInput aria-label="totalAcres"
                                thousandSeparator="," mask="separator" decimalMarker=".">
                              </mat-form-field>
                            </td>
                            <td>
                              <mat-form-field appearance="outline" class="example small-field">
                                <input formControlName="acresBurned" matInput aria-label="acresBurned"
                                  thousandSeparator="," mask="separator" decimalMarker=".">
                                </mat-form-field>
                              </td>
                              <td class="text-cell right">
                                {{ (100*subwatershed.controls[i].value.acresBurned /
                                subwatershed.controls[i].value.totalAcres) | number: '1.0-0' }}%
                              </td>
                              <td class="close-td">
                                <button class="delete-div" (click)="removeSubwatershed(i)" matTooltip='Remove'
                                  matTooltipClass="tooltipClass">
                                  <fa-icon [icon]="faCircleXmark" class="fa-regular fa-circle-mark delete-row-icon"
                                  aria-label="close"></fa-icon>
                                </button>
                              </td>
                            </tr>
                          }
                        </ng-container>
                      </table>
                    </div>

                    <div class="whole-line" style="padding-left: 17px;">
                      <button class="usa-button usa-button--secondary" (click)="addWaterShed()"><mat-icon>add</mat-icon>Add
                    row</button>
                  </div>
                  <div class="bar">&nbsp;</div>
                  <!-- Image upload -->
                  <image-upload [report_id]="report().report_id" [supporting_files]="report().supporting_files"
                  [saveSubject]="saveSubject" section="2" question="subwatershed"></image-upload>


                  <div class="text-div">
                    <b>@if (!optional()) {
                      <span class="red">*</span>
                    }Total Acres Burned</b>@if (optional()) {
                    <span class="optional">(Optional)</span>
                  }
                  <br>
                    <span class="gray-text">Acres burned by Ownership</span>
                  </div>
                  <!-- Yellow Banner -->
                  <ng-container *ngTemplateOutlet="gisWarn"></ng-container>

                  <!-- TABLE Ownership -->
                  <table>
                    <tr class="table-header">
                      <th>Ownership</th>
                      <th>Acres Burned</th>
                      <th>% Acres Burned</th>
                    </tr>
                    <ng-container formArrayName="acresBurned">
                      @for (data of acresBurned.controls; track data; let i = $index) {
                        <tr [formGroupName]="i" class="table-row">
                          @if (data.value.name == "Other Federal") {
                            <td style="width: 70%" class="text-cell" style="display: flex; height: 70px">
                              <div class="vert-center">{{ data.value.name }}</div>
                              <div class="vert-center">
                                <mat-icon
                                  matTooltip='Other Federal: ARMY, BIA, BLM, BOP, BPA, DOD, DOE, DOI, DOT, FHA, FWS, GSA, VA, USPS, USMC, USDA, USCG, USAF, USACE, USBR, OTHFE, HHS, IBWC, LG, NAVY, NOAA, NPS, NTVALL, NTVPIC'
                                  matTooltipClass="tooltipClass"
                                class="info-blue-header fa-regular fa-circle-info">info_outline</mat-icon>
                              </div>
                            </td>
                          } @else if (data.value.name == "Private") {
                            <td style="width: 70%" class="text-cell" style="display: flex; height: 70px">
                              <div class="vert-center">{{ data.value.name }}</div>
                              <div class="vert-center">
                                <mat-icon matTooltip='Private or Undetermined' matTooltipClass="tooltipClass"
                                class="info-blue-header fa-regular fa-circle-info">info_outline</mat-icon>
                              </div>
                            </td>
                          } @else {
                            <td style="width: 70%" class="text-cell">
                              {{ data.value.name }}
                            </td>
                          }
                          <td>
                            <mat-form-field appearance="outline" class="example">
                              <input formControlName="acresBurned" matInput aria-label="acresBurned"
                                thousandSeparator="," mask="separator" decimalMarker=".">
                              </mat-form-field>
                            </td>
                            <td>
                              <mat-form-field appearance="outline" class="example">
                                <input formControlName="percentBurned" matInput aria-label="percentBurned" mask="percent"
                                  suffix="%">
                                </mat-form-field>
                              </td>
                            </tr>
                          }
                          <tr class="sum-row">
                            <td class="text-cell">
                              Total Acres Burned
                            </td>
                            <td class="right">
                              {{ sumAcresBurned() | number: '' }}
                            </td>
                            <td class="right">
                              100%
                            </td>
                          </tr>
                        </ng-container>
                      </table>
                      <div class="bar">&nbsp;</div>
                      <!-- Image upload -->
                      <image-upload [report_id]="report().report_id" [supporting_files]="report().supporting_files"
                      [saveSubject]="saveSubject" section="2" question="acres_burned"></image-upload>

                      <div class="text-div">
                        <b>@if (!optional()) {
                          <span class="red">*</span>
                        }Vegetation Types</b>@if (optional()) {
                        <span class="optional">(Optional)</span>
                      }
                      <br>
                        <span class="gray-text">Please describe the major plant communities or vegetation types in the burned area,
                        include appropriate fire recurrence information if applicable.</span>
                        <br>
                        </div>
                        <!-- Rich Text input -->
                        <span class="gray-text tip-text"><b>Tip:</b> Use Ctrl+Shift+V to paste as plain text.</span>
                        <div class="NgxEditor__Wrapper">
                          <ngx-editor-menu [editor]="editor1" [toolbar]="toolbar"></ngx-editor-menu>
                          <ngx-editor formControlName="vegetation_types" [editor]="editor1" placeholder="Enter Description" required
                          (ngModelChange)="onChangeEditor($event, 'vegetation_types')"></ngx-editor>
                        </div>
                        <span class="gray-text text-count"
                          [ngClass]="{ 'text-count-red': (form.controls.vegetation_types.invalid && form.controls.vegetation_types.touched) }">
                          {{form.controls.vegetation_types.value?.length ?? 0 | number:'1.0':'en-US'}} / 6,000 characters used
                        </span>
                        @if (form.controls.vegetation_types.invalid && form.controls.vegetation_types.touched && (form.controls.vegetation_types.value?.length ?? 0) > 0) {
                          <div
                            class="editor-error">
                            <fa-icon [icon]="faWarning" class="faWarning" aria-label="Warning"></fa-icon> Please shorten your text or
                            remove hidden formatting (e.g., paste as plain text using Ctrl+Shift+V).
                          </div>
                        }

                        <!-- Image upload -->
                        <image-upload [report_id]="report().report_id" [supporting_files]="report().supporting_files"
                        [saveSubject]="saveSubject" section="2" question="vegetation_types"></image-upload>

                        <div class="text-div">
                          <b>@if (!optional()) {
                            <span class="red">*</span>
                          }Dominant Soils</b>@if (optional()) {
                          <span class="optional">(Optional)</span>
                        }
                        <br>
                          <span class="gray-text">Please describe the soil types and topography that best characterizes the burned
                          area. Use soil name that best reflect soil characteristics and level of available information.</span>
                        </div>
                        <!-- Rich Text input -->
                        <span class="gray-text tip-text"><b>Tip:</b> Use Ctrl+Shift+V to paste as plain text.</span>
                        <div class="NgxEditor__Wrapper">
                          <ngx-editor-menu [editor]="editor2" [toolbar]="toolbar"></ngx-editor-menu>
                          <ngx-editor formControlName="dominant_soils" [editor]="editor2" placeholder="Enter Description" required
                          (ngModelChange)="onChangeEditor($event, 'dominant_soils')"></ngx-editor>
                        </div>
                        <span class="gray-text text-count"
                          [ngClass]="{ 'text-count-red': (form.controls.dominant_soils.invalid && form.controls.dominant_soils.touched) }">{{form.controls.dominant_soils.value?.length
                        ?? 0 | number:'1.0':'en-US'}} / 6,000 characters used</span>
                        @if (form.controls.dominant_soils.invalid && form.controls.dominant_soils.touched && (form.controls.dominant_soils.value?.length ?? 0) > 0) {
                          <div
                            class="editor-error">
                            <fa-icon [icon]="faWarning" class="faWarning" aria-label="Warning"></fa-icon> Please shorten your text or
                            remove hidden formatting (e.g., paste as plain text using Ctrl+Shift+V).
                          </div>
                        }
                        <image-upload [report_id]="report().report_id" [supporting_files]="report().supporting_files"
                        [saveSubject]="saveSubject" section="2" question="dominant_soils"></image-upload>

                        <div class="text-div">
                          <b>@if (!optional()) {
                            <span class="red">*</span>
                          }Geologic Types</b>@if (optional()) {
                          <span class="optional">(Optional)</span>
                        }
                        <br>
                          <span class="gray-text">Please describe the geologic types and geomorphology that best characterizes the
                          burned area.</span>
                        </div>
                        <!-- Rich Text input -->
                        <span class="gray-text tip-text"><b>Tip:</b> Use Ctrl+Shift+V to paste as plain text.</span>
                        <div class="NgxEditor__Wrapper">
                          <ngx-editor-menu [editor]="editor3" [toolbar]="toolbar"></ngx-editor-menu>
                          <ngx-editor formControlName="geologic_types" [editor]="editor3" placeholder="Enter Description" required
                          (ngModelChange)="onChangeEditor($event, 'geologic_types')"></ngx-editor>
                        </div>
                        <span class="gray-text text-count"
                          [ngClass]="{ 'text-count-red': (form.controls.geologic_types.invalid && form.controls.geologic_types.touched) }">{{form.controls.geologic_types.value?.length
                        ?? 0 | number:'1.0':'en-US'}} / 6,000 characters used</span>
                        @if (form.controls.geologic_types.invalid && form.controls.geologic_types.touched && (form.controls.geologic_types.value?.length ?? 0) > 0) {
                          <div
                            class="editor-error">
                            <fa-icon [icon]="faWarning" class="faWarning" aria-label="Warning"></fa-icon> Please shorten your text or
                            remove hidden formatting (e.g., paste as plain text using Ctrl+Shift+V).
                          </div>
                        }
                        <image-upload [report_id]="report().report_id" [supporting_files]="report().supporting_files"
                        [saveSubject]="saveSubject" section="2" question="geologic_types"></image-upload>

                        <div class="text-div">
                          <b>@if (!optional()) {
                            <span class="red">*</span>
                          }Miles of Stream Channels by Type</b>@if (optional()) {
                          <span class="optional">(Optional)</span>
                        }
                        <br>
                          <span class="gray-text">The table below shows National NHD data. A value of '0' in 'Ephemeral' may mean data
                          has not been mapped.</span>
                          <br>
                            <span class="gray-text">Enter "N/A" if data is not available, not mapped, not applicable, or not
                            assessed.</span>
                          </div>
                          <ng-container *ngTemplateOutlet="gisWarn"></ng-container>
                          <!-- Table Steam Type -->
                          <table>
                            <tr class="table-header">
                              <th>Stream Type</th>
                              @for (forest of forests; track forest) {
                                <th class="right">
                                  {{ forest.forest_name }}
                                </th>
                              }
                              <th class="right last-column">Total</th>
                            </tr>
                            @for (stream of streamTypes; track stream) {
                              <tr class="table-row">
                                <td class="text-cell" style="width: 70%">
                                  {{ stream }}
                                </td>
                                <ng-container formArrayName="streams">
                                  @for (data of streams.controls; track data; let i = $index) {
                                    <td [formGroupName]="i">
                                      <mat-form-field appearance="outline" class="example small-field">
                                        <input class="right" [formControlName]="stream" matInput [attr.aria-label]="'Stream Type ' + i"
                                          thousandSeparator="," mask="separator||S/S" decimalMarker=".">
                                        </mat-form-field>
                                      </td>
                                    }
                                  </ng-container>
                                  <td class="right last-column">
                                    <b>{{ sumStream(stream) | number: '' }}</b>
                                  </td>
                                </tr>
                              }
                            </table>

                            <div class="text-div">
                              <b>@if (!optional()) {
                                <span class="red">*</span>
                              }NFS Roads</b>@if (optional()) {
                              <span class="optional">(Optional)</span>
                            }
                            <br>
                              <span class="gray-text">National Forest Service Road Miles by Operational Maintenance level.</span>
                            </div>
                            <ng-container *ngTemplateOutlet="gisWarn"></ng-container>
                            <!-- Table Operational Maintenance Level -->
                            <table>
                              <tr class="table-header">
                                <th id="optionalMaitLvl">Operational Maintenance Level</th>
                                @for (forest of forests; track forest) {
                                  <th id="forest" class="right">
                                    {{ forest.forest_name }}
                                  </th>
                                }
                              </tr>
                              @for (level of maintenanceLevels; track level) {
                                <tr class="table-row">
                                  <td class="text-cell" style="width: 70%">
                                    {{ level }}
                                  </td>
                                  <ng-container formArrayName="maintenance">
                                    @for (data of maintenance.controls; track data; let i = $index) {
                                      <td [formGroupName]="i">
                                        <mat-form-field appearance="outline" class="example small-field">
                                          <input class="right" [formControlName]="level" matInput [attr.aria-label]="'Operational Maintenance Level ' + i"
                                            thousandSeparator="," mask="separator||S/S" decimalMarker="." >
                                          </mat-form-field>
                                        </td>
                                      }
                                    </ng-container>
                                  </tr>
                                }
                                <tr class="sum-row">
                                  <td class="text-cell">
                                    Total Road Miles
                                  </td>
                                  @for (forest of forests; track forest; let i = $index) {
                                    <th id="totalRoadMiles" class="right text-cell">
                                      {{ sumRoadMiles(i) | number: '' }}
                                    </th>
                                  }
                                </tr>
                              </table>

                              <div class="text-div short-padding">
                                <b>@if (!optional()) {
                                  <span class="red">*</span>
                                }Other Roads</b>@if (optional()) {
                                <span class="optional">(Optional)</span>
                              }
                            </div>

                            <!-- Table Other Roads -->
                            <table class="small-table" role="presentation">
                              <tr class="table-row">
                                <td class="text-cell" style="width: 70%">
                                  Other Roads
                                </td>
                                <ng-container formArrayName="maintenance">
                                  @for (data of maintenance.controls; track data; let i = $index) {
                                    <td [formGroupName]="i">
                                      <mat-form-field appearance="outline" class="example small-field">
                                        <input class="right" formControlName="other_roads" matInput aria-label="Other Roads"
                                          thousandSeparator="," mask="separator||S/S" decimalMarker=".">
                                        </mat-form-field>
                                      </td>
                                    }
                                  </ng-container>
                                </tr>
                              </table>
                              <div class="side-line">
                                <div class="text-div">
                                  <b>Additional Details</b>
                                  <!-- Fancy Text -->
                                  <span class="gray-text tip-text"><b>Tip:</b> Use Ctrl+Shift+V to paste as plain text.</span>
                                  <div class="NgxEditor__Wrapper">
                                    <ngx-editor-menu [editor]="editor4" [toolbar]="toolbar"></ngx-editor-menu>
                                    <ngx-editor formControlName="other_roads" [editor]="editor4"
                                    placeholder="Enter Description"></ngx-editor>
                                  </div>
                                  <span class="gray-text text-count"
                                    [ngClass]="{ 'text-count-red': form.controls.other_roads.invalid }">{{form.controls.other_roads.value?.length
                                  ?? 0 | number:'1.0':'en-US'}} / 6,000 characters used</span>
                                  @if (form.controls.other_roads.invalid) {
                                    <div class="editor-error">
                                      <fa-icon [icon]="faWarning" class="faWarning" aria-label="Warning"></fa-icon> Please shorten your
                                      text or remove hidden formatting (e.g., paste as plain text using Ctrl+Shift+V).
                                    </div>
                                  }
                                </div>
                              </div>

                              <div class="text-div">
                                <b>@if (!optional()) {
                                  <span class="red">*</span>
                                }National Forest System Trails</b>@if (optional()) {
                                <span class="optional">(Optional)</span>
                              }
                              <br>
                                <span class="gray-text">Forest Service Terrestrial Trail Miles by Type</span>
                              </div>
                              <ng-container *ngTemplateOutlet="gisWarn"></ng-container>
                              <!-- Table Trail Type -->
                              <table>
                                <tr class="table-header">
                                  <th>Trail Type</th>
                                  @for (forest of forests; track forest) {
                                    <th class="right">
                                      {{ forest.forest_name }}
                                    </th>
                                  }
                                  <th class="right last-column">Total</th>
                                </tr>
                                @for (trail of trailTypes; track trail) {
                                  <tr class="table-row">
                                    <td class="text-cell" style="width: 70%">
                                      {{ trail }}
                                    </td>
                                    <ng-container formArrayName="trails">
                                      @for (data of trails.controls; track data; let i = $index) {
                                        <td [formGroupName]="i">
                                          <mat-form-field appearance="outline" class="example small-field">
                                            <input class="right" [formControlName]="trail" matInput [attr.aria-label]="'Trail Type ' + i"
                                              thousandSeparator="," mask="separator||S/S" decimalMarker=".">
                                            </mat-form-field>
                                          </td>
                                        }
                                      </ng-container>
                                      <td class="right last-column">
                                        <b>{{ sumTrail(trail) | number: '' }}</b>
                                      </td>
                                    </tr>
                                  }
                                </table>
                              }
                            </mat-card>
                          </main>

                          <ng-template #gisWarn>
                            <div class="yellow-warning">
                              <fa-icon [icon]="faWarning" class="faWarning" aria-label="Warning"></fa-icon>
                              <div class="support-text">
                                <b>Data source:</b> GIS data. Manual edits made below will be overwritten when GIS data is updated.
                              </div>
                            </div>
                          </ng-template>
